import React, {useState, useRef, useEffect, useCallback} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import messages from '../../../res/translations/messages';
import {fetchSurveyTags} from '../../actions/action_survey';
import {surveyTagsSelector} from '../../reducers/surveyTagsReducer';

const TAG_BG = '#119BD6';
const TAG_COLOR = '#fff';

const SurveyTagsField = ({value = [], onChange}) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const suggestions = useSelector(surveyTagsSelector);

    const [inputValue, setInputValue] = useState('');
    const [focused, setFocused] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const inputRef = useRef(null);
    const blurTimerRef = useRef(null);

    useEffect(() => {
        if (!inputValue.trim()) return;
        const timer = setTimeout(() => {
            dispatch(fetchSurveyTags(inputValue));
            setActiveIndex(-1);
        }, 250);
        return () => clearTimeout(timer);
    }, [inputValue, dispatch]);

    const visibleSuggestions = suggestions.filter((s) => !value.includes(s));
    const showDropdown = focused && inputValue.trim().length > 0 && visibleSuggestions.length > 0;

    const addTag = useCallback((tag) => {
        const trimmed = tag.trim().toLowerCase().replace(/\s+/g, '');
        if (trimmed.length < 3) return;
        if (!value.includes(trimmed)) {
            onChange([...value, trimmed]);
        }
        setInputValue('');
        setActiveIndex(-1);
        if (inputRef.current) inputRef.current.focus();
    }, [value, onChange]);

    const removeTag = useCallback((tag) => {
        onChange(value.filter((t) => t !== tag));
    }, [value, onChange]);

    const handleContainerClick = useCallback(() => {
        if (inputRef.current) inputRef.current.focus();
    }, []);

    const handleInputChange = useCallback((e) => {
        setInputValue(e.target.value.toLowerCase().replace(/\s/g, ''));
    }, []);

    const handleFocus = useCallback(() => {
        if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
        setFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        blurTimerRef.current = setTimeout(() => setFocused(false), 150);
    }, []);

    const handleKeyDown = useCallback((e) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex((i) => Math.min(i + 1, visibleSuggestions.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex((i) => Math.max(i - 1, -1));
                break;
            case 'Enter':
                e.preventDefault();
                if (activeIndex >= 0 && visibleSuggestions[activeIndex]) {
                    addTag(visibleSuggestions[activeIndex]);
                } else if (inputValue.trim()) {
                    addTag(inputValue);
                }
                break;
            case 'Escape':
                setFocused(false);
                setActiveIndex(-1);
                break;
            case 'Backspace':
                if (!inputValue && value.length > 0) removeTag(value[value.length - 1]);
                break;
            default:
                break;
        }
    }, [activeIndex, visibleSuggestions, inputValue, addTag, removeTag, value]);

    return (
        <div style={{position: 'relative'}}>
            {/* eslint-disable-next-line react/jsx-no-bind */}
            <div
                className="form-control"
                style={{display: 'flex', flexWrap: 'wrap', gap: '4px', height: 'auto', minHeight: '38px', padding: '4px 8px', cursor: 'text'}}
                onClick={handleContainerClick}
            >
                {value.map((tag) => (
                    <span
                        key={tag}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '0.82em',
                            fontWeight: 500,
                            padding: '3px 8px',
                            borderRadius: '3px',
                            backgroundColor: TAG_BG,
                            color: TAG_COLOR,
                            lineHeight: 1.4,
                        }}
                    >
                        {tag}
                        <button
                            type="button"
                            // eslint-disable-next-line react/jsx-no-bind
                            onClick={(e) => {
                                e.stopPropagation();
                                removeTag(tag);
                            }}
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: '0 0 0 2px',
                                cursor: 'pointer',
                                color: TAG_COLOR,
                                lineHeight: 1,
                                fontSize: '1.1em',
                                opacity: 0.85,
                            }}
                            aria-label={intl.formatMessage(messages.surveys.form.tags.removeTag, {tag})}
                        >
                            ×
                        </button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={value.length === 0 ? intl.formatMessage(messages.surveys.form.tags.placeholder) : ''}
                    style={{border: 'none', outline: 'none', flex: 1, minWidth: '120px', background: 'transparent', padding: '2px 0'}}
                />
            </div>
            {showDropdown && (
                <ul
                    className="list-group"
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        border: '1px solid rgba(0,0,0,.125)',
                        borderTop: 'none',
                        borderRadius: '0 0 4px 4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        margin: 0,
                        padding: 0,
                    }}
                >
                    {visibleSuggestions.map((suggestion, index) => (
                        <li
                            key={suggestion}
                            className={`list-group-item list-group-item-action${activeIndex === index ? ' active' : ''}`}
                            style={{cursor: 'pointer', padding: '8px 12px'}}
                            // eslint-disable-next-line react/jsx-no-bind
                            onMouseDown={(e) => {
                                e.preventDefault();
                                addTag(suggestion);
                            }}
                            // eslint-disable-next-line react/jsx-no-bind
                            onMouseEnter={() => setActiveIndex(index)}
                        >
                            {suggestion}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default SurveyTagsField;
